import { Col, DatePicker, Form, Row, Select, Typography } from "antd"
import dayjs from "dayjs"
import { useEffect } from "react"

import { classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import { SFormHeader } from "#blocks/Form/formComponents"
import SessionSelect from "#blocks/Form/SessionSelect"
import { Student } from "#classes/Student"
import { TRYOUT_FEES_GROUPED } from "#constants/tryoutOptions"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { DropdownOption } from "#customTypes/composedTypes/genericFormTypes"
import { StudentSkillType } from "#customTypes/composedTypes/studentTypes"
import { TryoutProgressFormData } from "#customTypes/composedTypes/tryoutTypes"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { STANDARD_DATE_FORMAT } from "#utils/dateHelpers"
import { formatSemester, getCurrentSemester } from "#utils/semesterHelpers"
import { setNestedFormField } from "#utils/setNestedFormField"
import {
  formatSkillLevelWithPending,
  getAllSkillLevelsWithPending,
} from "#utils/studentSkillHelpers"

interface TryoutProgressStep0Props {
  tryoutSessionOptions: DropdownOption[]
}

const TryoutProgressStep0 = ({
  tryoutSessionOptions,
}: TryoutProgressStep0Props) => {
  const form = Form.useFormInstance<TryoutProgressFormData>()

  const studentId = Form.useWatch("studentId", form)
  const sessionId = Form.useWatch("sessionId", form)
  const tryoutFilterDate = Form.useWatch("tryoutFilterDate", form)
  const tryoutFeeId = Form.useWatch(["step0", "tryoutFeeId"], form)

  // Fetch student via ID from form state
  const student = useGetOneStudent(studentId)

  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionId
  )

  // Derive semester from tryout filter date
  const semester = tryoutFilterDate
    ? getCurrentSemester(
        dayjs(tryoutFilterDate).format(STANDARD_DATE_FORMAT),
        STANDARD_DATE_FORMAT
      )
    : undefined

  // Update semester when tryout filter date changes
  useEffect(() => {
    if (semester) {
      form.setFieldsValue({ semester })
    }
  }, [semester, form])

  // Auto-check "Session selected" when sessionId is truthy
  useEffect(() => {
    if (sessionId) {
      setNestedFormField(form, ["step0", "hasSelectedSession"], true)
    }
  }, [sessionId, form])

  // Auto-check "Fee paid" when tryoutFeeId has value
  useEffect(() => {
    if (tryoutFeeId) {
      setNestedFormField(form, ["step0", "isTryoutFeePaid"], true)
    }
  }, [tryoutFeeId, form])

  // When session options refresh, keep a valid current selection;
  // otherwise select the first non-disabled option.
  useEffect(() => {
    if (!tryoutSessionOptions.length) return

    const currentOption = tryoutSessionOptions.find(
      (option) => option.value === sessionId
    )
    const hasValidCurrentSelection = !!currentOption && !currentOption.disabled

    if (hasValidCurrentSelection) return

    const firstEnabledOption = tryoutSessionOptions.find(
      (option) => !option.disabled && option.value
    )
    if (!firstEnabledOption?.value) return

    form.setFieldValue("sessionId", firstEnabledOption.value)
  }, [form, sessionId, tryoutSessionOptions])

  // Levels as of the tryout's semester, each with the promotion an approved
  // eval has already recorded for the semester after (see studentSkillHelpers).
  const currentLevels = getAllSkillLevelsWithPending(student, semester)

  return (
    <Flex col style={{ padding: "20px 30px", width: 800 }}>
      <Row gutter={40}>
        <Col span={24}>
          <SFormHeader>Student Info</SFormHeader>
          <Row gutter={32}>
            <Col span={10}>
              <Form.Item label="Name">
                <Typography.Text strong>
                  {student
                    ? new Student(student).getNameAndDob()
                    : "Loading..."}
                </Typography.Text>
              </Form.Item>
            </Col>
            {student && (
              <>
                <Col span={3}>
                  <Form.Item label="Core">
                    <Typography.Text>
                      {formatSkillLevelWithPending(
                        currentLevels[StudentSkillType.core]
                      ) || "—"}
                    </Typography.Text>
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Digital">
                    <Typography.Text>
                      {formatSkillLevelWithPending(
                        currentLevels[StudentSkillType.digital]
                      ) || "—"}
                    </Typography.Text>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Sculpture">
                    <Typography.Text>
                      {formatSkillLevelWithPending(
                        currentLevels[StudentSkillType.sculpture]
                      ) || "—"}
                    </Typography.Text>
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          {/* Session Selection */}
          <SFormHeader>Tryout Session</SFormHeader>
          <Row gutter={32}>
            <Col span={10}>
              <Form.Item
                label="Find sessions after (up to 2 weeks)"
                name="tryoutFilterDate"
              >
                <DatePicker
                  format="M/D/YY - dddd"
                  style={{ width: "100%" }}
                  minDate={dayjs("2016-01-01")}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Semester">
                <Typography.Text>{formatSemester(semester)}</Typography.Text>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Current session time">
                <Typography.Text>
                  {session
                    ? `${dayjs(session.eventStart).format("M/D/YY h:mm A")} - ${dayjs(session.eventEnd).format("h:mm A")}`
                    : "Loading..."}
                </Typography.Text>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={20}>
              <Form.Item
                name="sessionId"
                label="Change tryout session"
                rules={[
                  {
                    required: true,
                    message: "Please select a tryout session!",
                  },
                ]}
              >
                <SessionSelect
                  placeholder="Select a tryout session"
                  options={tryoutSessionOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tryout Fees */}
          <SFormHeader>Tryout Fee</SFormHeader>
          <Row>
            <Col span={20}>
              <Flex col gap={10}>
                <Typography.Text strong>
                  {`Can pay with Zelle, Cash, Check, Credit card (don't charge extra for over $50)`}
                </Typography.Text>
                <Flex gap={40} style={{ alignItems: "center" }}>
                  <CheckboxWrapper
                    name={["step0", "isTryoutFeePaid"]}
                    label="Tryout fee paid"
                  />
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 14, height: 34 }}
                  >
                    Receipt already sent
                  </Typography.Text>
                </Flex>
                <Flex>
                  <Form.Item name={["step0", "tryoutFeeId"]}>
                    <Select
                      options={TRYOUT_FEES_GROUPED}
                      style={{ width: 614 }}
                      placeholder="Select a tryout fee"
                    />
                  </Form.Item>
                </Flex>
              </Flex>
            </Col>
          </Row>
        </Col>
      </Row>
    </Flex>
  )
}

export default TryoutProgressStep0
